import React, { useState } from "react";
import { Button, message, Modal, Table, Row, Col } from "antd";
import { checkActionAllowed, isValid, isValidArray } from "../../utils/utilities"
import { useDispatch } from "react-redux";
import { loadingAction } from "../../actions/loading-actions";
import { ACTION_VIEW_GROUP_BY_BRANCH_REPORT_ALLOWED } from "../../utils/constant";
import { fetchTripSheet } from "../../actions/reports.action";
import * as XLSX from 'xlsx';
import { pdf } from '@react-pdf/renderer';
import { Document, Page, Text, View, StyleSheet, Font } from '@react-pdf/renderer';
import { getUIDateFormat } from "../../utils/date.utils";
import { roundNumber } from "../../utils/math.utils";

// CSS for summary row styling
const summaryRowStyle = `
    .summary-row {
        background-color: #f0f8ff !important;
        font-weight: bold !important;
    }
    .summary-row td {
        background-color: #f0f8ff !important;
        font-weight: bold !important;
        border-top: 2px solid #1890ff !important;
    }
`;

// PDF Styles
const styles = StyleSheet.create({
    page: {
        flexDirection: 'column',
        backgroundColor: '#FFFFFF',
        padding: 20,
        fontSize: 9,
    },
    header: {
        marginBottom: 15,
        borderBottom: 2,
        borderBottomColor: '#1890ff',
        paddingBottom: 8,
    },
    title: {
        fontSize: 16,
        fontWeight: 'bold',
        color: '#1890ff',
        marginBottom: 8,
    },
    tripInfoSection: {
        marginBottom: 4,
    },
    tripInfoRow: {
        flexDirection: 'row',
        justifyContent: 'space-between',
        marginBottom: 3,
    },
    tripInfoLabel: {
        fontSize: 9,
        color: '#333',
    },
    branchSection: {
        marginBottom: 10,
    },
    branchTitle: {
        fontSize: 12,
        fontWeight: 'bold',
        color: '#333',
        marginTop: 10,
        marginBottom: 6,
        backgroundColor: '#f0f0f0',
        padding: 6,
        borderRadius: 3,
        borderLeft: '3px solid #1890ff',
    },
    table: {
        display: 'table',
        width: 'auto',
        borderStyle: 'solid',
        borderWidth: 1,
        borderColor: '#d9d9d9',
        marginBottom: 15,
    },
    tableRow: {
        flexDirection: 'row',
        borderBottomWidth: 1,
        borderBottomColor: '#d9d9d9',
        borderBottomStyle: 'solid',
    },
    tableHeader: {
        backgroundColor: '#fafafa',
        fontWeight: 'bold',
    },
    tableCell: {
        padding: 4,
        borderRightWidth: 1,
        borderRightColor: '#d9d9d9',
        borderRightStyle: 'solid',
        fontSize: 7,
        minHeight: 20,
    },
    tableCellHeader: {
        padding: 4,
        borderRightWidth: 1,
        borderRightColor: '#d9d9d9',
        borderRightStyle: 'solid',
        fontSize: 7,
        fontWeight: 'bold',
        minHeight: 20,
    },
    statusConfirmed: {
        color: '#52c41a',
        fontWeight: 'bold',
    },
    statusBlocked: {
        color: '#ff4d4f',
        fontWeight: 'bold',
    },
    currency: {
        textAlign: 'right',
    }
});

const calculateBranchSummary = (branchData) => {
    if (!isValidArray(branchData)) {
        return {
            totalSeats: 0,
            totalFare: 0,
            totalGST: 0,
            totalCommission: 0,
            totalAmountPaid: 0
        };
    }

    const summary = branchData.reduce((acc, item) => {
        return {
            totalSeats: acc.totalSeats + (item.totalSeats || 0),
            totalFare: acc.totalFare + (item.totalFare || 0),
            totalGST: acc.totalGST + (item.totalGST || 0),
            totalCommission: acc.totalCommission + (item.totalCommission || 0),
            totalAmountPaid: acc.totalAmountPaid + (item.totalAmountPaid || 0)
        };
    }, {
        totalSeats: 0,
        totalFare: 0,
        totalGST: 0,
        totalCommission: 0,
        totalAmountPaid: 0
    });

    // Apply roundNumber to financial values
    return {
        totalSeats: summary.totalSeats,
        totalFare: roundNumber(summary.totalFare),
        totalGST: roundNumber(summary.totalGST),
        totalCommission: roundNumber(summary.totalCommission),
        totalAmountPaid: roundNumber(summary.totalAmountPaid)
    };
}

// PDF Document Component
const GroupByBranchPDF = ({ branchWiseData, tripDetails, vehicleInfo }) => (
    <Document>
        <Page size="A4" orientation="landscape" style={styles.page} wrap={false}>
            {/* Header - matching modal structure */}
            <View style={styles.header}>
                <Text style={styles.title}>{global.operatorName} - Branch Wise Report</Text>
                
                {/* Trip Information - matching modal layout */}
                <View style={styles.tripInfoSection}>
                    <Text style={styles.tripInfoLabel}>Trip Name: {tripDetails?.description || 'N/A'}</Text>
                </View>
                
                <View style={styles.tripInfoRow}>
                    <Text style={styles.tripInfoLabel}>From: {tripDetails?.origin || 'N/A'}</Text>
                    <Text style={styles.tripInfoLabel}>To: {tripDetails?.destination || 'N/A'}</Text>
                </View>
                
                <View style={styles.tripInfoRow}>
                    <Text style={styles.tripInfoLabel}>Travel Date: {getUIDateFormat(tripDetails?.start_date) || 'N/A'}</Text>
                    <Text style={styles.tripInfoLabel}>Dep Time: {tripDetails?.start_time || 'N/A'}</Text>
                </View>
                
                <View style={styles.tripInfoRow}>
                    <Text style={styles.tripInfoLabel}>Vehicle No: {vehicleInfo?.vechicle_no || 'N/A'}</Text>
                </View>
            </View>

            {/* All Branches in Single Page */}
            {Object.keys(branchWiseData).map((branch, branchIndex) => {
                const branchData = branchWiseData[branch];
                if (branchData.length === 0) return null;

                return (
                    <View key={branchIndex} style={styles.branchSection}>
                        {/* Branch Title - matching modal styling */}
                        <Text style={styles.branchTitle}>{branch}</Text>

                        {/* Table - matching modal columns exactly */}
                        <View style={styles.table}>
                            {/* Table Header */}
                            <View style={[styles.tableRow, styles.tableHeader]}>
                                <Text style={[styles.tableCellHeader, { width: '12%' }]}>Agent</Text>
                                <Text style={[styles.tableCellHeader, { width: '10%' }]}>PNR</Text>
                                <Text style={[styles.tableCellHeader, { width: '16%' }]}>Origin</Text>
                                <Text style={[styles.tableCellHeader, { width: '16%' }]}>Destination</Text>
                                <Text style={[styles.tableCellHeader, { width: '16%' }]}>Passenger Name</Text>
                                <Text style={[styles.tableCellHeader, { width: '12%' }]}>Phone Number</Text>
                                <Text style={[styles.tableCellHeader, { width: '12%' }]}>Seat Numbers</Text>
                                <Text style={[styles.tableCellHeader, { width: '8%' }]}>Total Seats</Text>
                                <Text style={[styles.tableCellHeader, { width: '10%' }]}>Total Fare</Text>
                                <Text style={[styles.tableCellHeader, { width: '8%' }]}>Total GST</Text>
                                <Text style={[styles.tableCellHeader, { width: '12%' }]}>Total Commission</Text>
                                <Text style={[styles.tableCellHeader, { width: '12%' }]}>Total Amount Paid</Text>
                            </View>

                            {/* Table Rows */}
                            {branchData.map((item, index) => (
                                <View key={index} style={styles.tableRow}>
                                    <Text style={[styles.tableCell, { width: '12%' }]}>{item.agent}</Text>
                                    <Text style={[styles.tableCell, { width: '10%' }]}>{item.pnr}</Text>
                                    <Text style={[styles.tableCell, { width: '16%' }]}>{item.origin}</Text>
                                    <Text style={[styles.tableCell, { width: '16%' }]}>{item.dropCity}</Text>
                                    <Text style={[styles.tableCell, { width: '16%' }]}>{item.passengerName}</Text>
                                    <Text style={[styles.tableCell, { width: '12%' }]}>{item.phoneNumber}</Text>
                                    <Text style={[styles.tableCell, { width: '12%' }]}>{item.seatNumbers}</Text>
                                    <Text style={[styles.tableCell, { width: '8%' }]}>{item.totalSeats}</Text>
                                    <Text style={[styles.tableCell, styles.currency, { width: '10%' }]}>₹{item.totalFare}</Text>
                                    <Text style={[styles.tableCell, styles.currency, { width: '8%' }]}>₹{item.totalGST}</Text>
                                    <Text style={[styles.tableCell, styles.currency, { width: '12%' }]}>₹{item.totalCommission}</Text>
                                    <Text style={[styles.tableCell, styles.currency, { width: '12%' }]}>₹{item.totalAmountPaid}</Text>
                                </View>
                            ))}
                            
                            {/* Summary Row */}
                            {(() => {
                                const summary = calculateBranchSummary(branchData);
                                
                                return (
                                    <View style={[styles.tableRow, { backgroundColor: '#f0f8ff', borderTop: '2px solid #1890ff' }]}>
                                        <Text style={[styles.tableCell, { width: '12%', fontWeight: 'bold' }]}>TOTAL</Text>
                                        <Text style={[styles.tableCell, { width: '10%' }]}></Text>
                                        <Text style={[styles.tableCell, { width: '16%' }]}></Text>
                                        <Text style={[styles.tableCell, { width: '16%' }]}></Text>
                                        <Text style={[styles.tableCell, { width: '16%' }]}></Text>
                                        <Text style={[styles.tableCell, { width: '12%' }]}></Text>
                                        <Text style={[styles.tableCell, { width: '12%' }]}></Text>
                                        <Text style={[styles.tableCell, { width: '8%', fontWeight: 'bold' }]}>{summary.totalSeats}</Text>
                                        <Text style={[styles.tableCell, styles.currency, { width: '10%', fontWeight: 'bold' }]}>₹{summary.totalFare}</Text>
                                        <Text style={[styles.tableCell, styles.currency, { width: '8%', fontWeight: 'bold' }]}>₹{summary.totalGST}</Text>
                                        <Text style={[styles.tableCell, styles.currency, { width: '12%', fontWeight: 'bold' }]}>₹{summary.totalCommission}</Text>
                                        <Text style={[styles.tableCell, styles.currency, { width: '12%', fontWeight: 'bold' }]}>₹{summary.totalAmountPaid}</Text>
                                    </View>
                                );
                            })()}
                        </View>
                    </View>
                );
            })}
        </Page>
    </Document>
);

export const GroupByBranchComponent = (props) => {

    const {
        tripId
    } = props;

    const dispatch = useDispatch();
    const [isModalVisible, setIsModalVisible] = useState(false);
    const [modalData, setModalData] = useState(null);
    const [modalFilteredData, setModalFilteredData] = useState(null);
    const setLoading=(bool)=>{
        dispatch(loadingAction(bool));
    }

    const filterData = (data) => {
        let branchWiseAgents = {}, agentWisePnr = {}, pnrWiseConfirmedData = {}, pnrWiseBlockedData = {}, operatorBranches = [];
        if(isValidArray(data.tripSheet)){
            data.tripSheet.forEach(item => {
                let branchName = item.branch !== null ? item.branch : ["OTA","Website","website"].includes(item.agent_privilege) ? item.agent_privilege : item.agent_name == "QwikBus" ? "QwikBus" : "Others";
                if (item.branch != null && !operatorBranches.includes(item.branch)){
                    operatorBranches.push(item.branch);
                }
                if(!agentWisePnr[item.agent_name]){
                    agentWisePnr[item.agent_name] = [item.pnr];
                }
                else {
                    if(!agentWisePnr[item.agent_name].includes(item.pnr)){
                        agentWisePnr[item.agent_name].push(item.pnr);
                    }
                }
                if(!branchWiseAgents[branchName]){
                    branchWiseAgents[branchName] = [item.agent_name];
                }
                else {
                    if(!branchWiseAgents[branchName].includes(item.agent_name)){
                        branchWiseAgents[branchName].push(item.agent_name);
                    }
                }
                if (item.traveller_status == "Confirmed"){
                    if(!pnrWiseConfirmedData[item.pnr]){
                        let itemData = {...item};
                        itemData.seatNums = [item.seat_number];
                        itemData.totalSeats = 1;
                        itemData.totalFare = item.fare;
                        itemData.totalGST = item.gst;
                        itemData.totalCommission = item.commission;
                        itemData.totalAmountPaid = item.amount_paid;
                        pnrWiseConfirmedData[item.pnr] = itemData;
                    }
                    else {
                        pnrWiseConfirmedData[item.pnr].seatNums.push(item.seat_number);
                        pnrWiseConfirmedData[item.pnr].totalSeats++;
                        pnrWiseConfirmedData[item.pnr].totalFare += item.fare;
                        pnrWiseConfirmedData[item.pnr].totalGST += item.gst;
                        pnrWiseConfirmedData[item.pnr].totalCommission += item.commission;
                        pnrWiseConfirmedData[item.pnr].totalAmountPaid += item.amount_paid;
                    }
                }
                else if (item.traveller_status == "Blocked"){
                    if(!pnrWiseBlockedData[item.pnr]){
                        let itemData = {...item};
                        itemData.seatNums = [item.seat_number];
                        itemData.totalSeats = 1;
                        itemData.totalFare = item.fare;
                        itemData.totalGST = item.gst;
                        itemData.totalCommission = item.commission;
                        itemData.totalAmountPaid = item.amount_paid;
                        pnrWiseBlockedData[item.pnr] = itemData;
                    }
                    else {
                        pnrWiseBlockedData[item.pnr].seatNums.push(item.seat_number);
                        pnrWiseBlockedData[item.pnr].totalSeats++;
                        pnrWiseBlockedData[item.pnr].totalFare += item.fare;
                        pnrWiseBlockedData[item.pnr].totalGST += item.gst;
                        pnrWiseBlockedData[item.pnr].totalCommission += item.commission;
                        pnrWiseBlockedData[item.pnr].totalAmountPaid += item.amount_paid;
                    }
                }
            });
        }
        let filteredData = {
            branchWiseAgents: branchWiseAgents,
            agentWisePnr: agentWisePnr,
            pnrWiseConfirmedData: pnrWiseConfirmedData,
            pnrWiseBlockedData: pnrWiseBlockedData,
            operatorBranches: operatorBranches
        };


        
        setModalFilteredData(filteredData);
    }

    const populateTableData = (filteredData) => {
        const branchWiseTableData = {};
        
        if (!isValid(filteredData)) {
            return branchWiseTableData;
        }

        // Get all branches from both operatorBranches and branchWiseAgents
        const allBranches = new Set();
        
        // Add branches from operatorBranches
        if (isValidArray(filteredData.operatorBranches)) {
            filteredData.operatorBranches.forEach(branch => allBranches.add(branch));
        }
        
        // Add branches from branchWiseAgents that are not in operatorBranches
        if (isValid(filteredData.branchWiseAgents)) {
            Object.keys(filteredData.branchWiseAgents).forEach(branch => allBranches.add(branch));
        }

        // Loop through all branches (operatorBranches + additional branches from branchWiseAgents)
        allBranches.forEach(branch => {
            branchWiseTableData[branch] = [];
            
            // Fetch agents from branchWiseAgents for this branch
            const agents = filteredData.branchWiseAgents[branch] || [];
            
            agents.forEach(agent => {
                // Fetch PNRs from agentWisePnr for this agent
                const pnrs = filteredData.agentWisePnr[agent] || [];
                
                pnrs.forEach(pnr => {
                    // Create table data from pnrWiseConfirmedData
                    if (filteredData.pnrWiseConfirmedData[pnr]) {
                        const confirmedData = filteredData.pnrWiseConfirmedData[pnr];
                        branchWiseTableData[branch].push({
                            key: `${branch}-${agent}-${pnr}-confirmed`,
                            agent: agent,
                            pnr: pnr,
                            status: 'Confirmed',
                            origin: confirmedData.pick_up_city,
                            dropCity: confirmedData.drop_city,
                            seatNumbers: confirmedData.seatNums.join(', '),
                            totalSeats: confirmedData.totalSeats,
                            totalFare: confirmedData.totalFare,
                            totalGST: confirmedData.totalGST,
                            totalCommission: confirmedData.totalCommission,
                            totalAmountPaid: confirmedData.totalAmountPaid,
                            passengerName: confirmedData.full_name,
                            phoneNumber: confirmedData.phone_number,
                            email: confirmedData.email
                        });
                    }
                    
                    // Create table data from pnrWiseBlockedData
                    if (filteredData.pnrWiseBlockedData[pnr]) {
                        const blockedData = filteredData.pnrWiseBlockedData[pnr];
                        branchWiseTableData[branch].push({
                            key: `${branch}-${agent}-${pnr}-blocked`,
                            agent: agent,
                            pnr: pnr,
                            status: 'Blocked',
                            origin: blockedData.pick_up_city,
                            dropCity: blockedData.drop_city,
                            seatNumbers: blockedData.seatNums.join(', '),
                            totalSeats: blockedData.totalSeats,
                            totalFare: roundNumber(blockedData.totalFare),
                            totalGST: roundNumber(blockedData.totalGST),
                            totalCommission: roundNumber(blockedData.totalCommission),
                            totalAmountPaid: roundNumber(blockedData.totalAmountPaid),
                            passengerName: blockedData.full_name,
                            phoneNumber: blockedData.phone_number,
                            email: blockedData.email
                        });
                    }
                });
            });
        });
        
        return branchWiseTableData;
    }

    const columns = [
        {
            title: 'Agent',
            dataIndex: 'agent',
            key: 'agent',
        },
        {
            title: 'PNR',
            dataIndex: 'pnr',
            key: 'pnr',
        },
        {
            title: 'Origin',
            dataIndex: 'origin',
            key: 'origin',
        },
        {
            title: 'Destination',
            dataIndex: 'dropCity',
            key: 'dropCity',
        },
        {
            title: 'Passenger Name',
            dataIndex: 'passengerName',
            key: 'passengerName',
        },
        {
            title: 'Phone Number',
            dataIndex: 'phoneNumber',
            key: 'phoneNumber',
        },
        {
            title: 'Seat Numbers',
            dataIndex: 'seatNumbers',
            key: 'seatNumbers',
        },
        {
            title: 'Total Seats',
            dataIndex: 'totalSeats',
            key: 'totalSeats',
        },
        {
            title: 'Total Fare',
            dataIndex: 'totalFare',
            key: 'totalFare',
            render: (amount) => `₹${amount}`,
        },
        {
            title: 'Total GST',
            dataIndex: 'totalGST',
            key: 'totalGST',
            render: (amount) => `₹${amount}`,
        },
        {
            title: 'Total Commission',
            dataIndex: 'totalCommission',
            key: 'totalCommission',
            render: (amount) => `₹${amount}`,
        },
        {
            title: 'Total Amount Paid',
            dataIndex: 'totalAmountPaid',
            key: 'totalAmountPaid',
            render: (amount) => `₹${amount}`,
        }
    ];

    const showModal = () => {
        fetchTripSheet(tripId,(data)=>{
            filterData(data);
            setModalData(data);
            setIsModalVisible(true);
        },(error)=>{
            message.error(error);
        });
        
    }

    const handleCancel = () => {
        setIsModalVisible(false);
    }

    const exportToExcel = () => {
        if (!isValid(modalFilteredData)) {
            message.error("No data available to export");
            return;
        }

        const branchWiseData = populateTableData(modalFilteredData);
        const workbook = XLSX.utils.book_new();
        const tripDetails = modalData?.tripDetails;
        const vehicleInfo = modalData?.vechicleInfo;

        // Prepare all data for single sheet
        const allData = [];
        
        // Add header information
        allData.push([`${global.operatorName} - Branch Wise Report`]);
        allData.push(['']);
        allData.push(['Trip Name:', tripDetails?.description || 'N/A']);
        allData.push(['From:', tripDetails?.origin || 'N/A']);
        allData.push(['To:', tripDetails?.destination || 'N/A']);
        allData.push(['Travel Date:', getUIDateFormat(tripDetails?.start_date) || 'N/A']);
        allData.push(['Dep Time:', tripDetails?.start_time || 'N/A']);
        allData.push(['Vehicle No:', vehicleInfo?.vechicle_no || 'N/A']);
        allData.push(['']);
        
        // Add table headers
        allData.push([
            'Branch', 'Agent', 'PNR', 'Origin', 'Destination', 'Passenger Name', 'Phone Number', 
            'Seat Numbers', 'Total Seats', 'Total Fare', 'Total GST', 
            'Total Commission', 'Total Amount Paid'
        ]);

        // Add all branch data with summary rows
        Object.keys(branchWiseData).forEach((branch, branchIndex) => {
            const branchData = branchWiseData[branch];
            
            // Add regular data rows
            branchData.forEach(item => {
                allData.push([
                    branch,
                    item.agent,
                    item.pnr,
                    item.origin,
                    item.dropCity,
                    item.passengerName,
                    item.phoneNumber,
                    item.seatNumbers,
                    item.totalSeats,
                    `₹${item.totalFare}`,
                    `₹${item.totalGST}`,
                    `₹${item.totalCommission}`,
                    `₹${item.totalAmountPaid}`
                ]);
            });
            
            // Add summary row for this branch
            const summary = calculateBranchSummary(branchData);
            allData.push([
                branch,
                'TOTAL',
                '',
                '',
                '',
                '',
                '',
                '',
                summary.totalSeats,
                `₹${summary.totalFare}`,
                `₹${summary.totalGST}`,
                `₹${summary.totalCommission}`,
                `₹${summary.totalAmountPaid}`
            ]);
            
            // Add empty row after each branch (except the last one)
            if (branchIndex < Object.keys(branchWiseData).length - 1) {
                allData.push(['', '', '', '', '', '', '', '', '', '', '', '', '']);
            }
        });

        // Create single worksheet with all data
        const worksheet = XLSX.utils.aoa_to_sheet(allData);
        
        // Set column widths
        const columnWidths = [
            { wch: 15 }, // Branch
            { wch: 15 }, // Agent
            { wch: 12 }, // PNR
            { wch: 15 }, // Origin
            { wch: 15 }, // Destination
            { wch: 20 }, // Passenger Name
            { wch: 15 }, // Phone Number
            { wch: 15 }, // Seat Numbers
            { wch: 12 }, // Total Seats
            { wch: 12 }, // Total Fare
            { wch: 12 }, // Total GST
            { wch: 15 }, // Total Commission
            { wch: 15 }  // Total Amount Paid
        ];
        worksheet['!cols'] = columnWidths;

        // Add worksheet to workbook
        XLSX.utils.book_append_sheet(workbook, worksheet, 'Group By Branch');

        // Generate filename with trip details
        const filename = `Branch_Report_${tripDetails?.origin || 'Origin'}_${tripDetails?.destination || 'Destination'}_${getUIDateFormat(tripDetails?.start_date) || 'Date'}.xlsx`;
        
        // Save the file
        XLSX.writeFile(workbook, filename);
        message.success("Excel file exported successfully!");
    };

    const exportToPDF = async () => {
        if (!isValid(modalFilteredData)) {
            message.error("No data available to export");
            return;
        }

        try {
            const branchWiseData = populateTableData(modalFilteredData);
            const tripDetails = modalData?.tripDetails;
            const vehicleInfo = modalData?.vechicleInfo;

            // Create PDF document
            const doc = <GroupByBranchPDF 
                branchWiseData={branchWiseData}
                tripDetails={tripDetails}
                vehicleInfo={vehicleInfo}
            />;

            // Generate PDF blob
            const blob = await pdf(doc).toBlob();
            
            // Create download link
            const url = URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            
            // Generate filename
            const filename = `Branch_Report_${tripDetails?.origin || 'Origin'}_${tripDetails?.destination || 'Destination'}_${getUIDateFormat(tripDetails?.start_date) || 'Date'}.pdf`;
            link.download = filename;
            
            // Trigger download
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            
            // Clean up
            URL.revokeObjectURL(url);
            
            message.success("PDF file exported successfully!");
        } catch (error) {
            console.error('PDF export error:', error);
            message.error("Failed to export PDF file");
        }
    };

    const handleExport = (type) => {
        if (type === 'excel') {
            exportToExcel();
        } else if (type === 'pdf') {
            exportToPDF();
        }
    }

    const renderModalContent = () => {
        if (isValid(modalData) && isValid(modalFilteredData)){
            return (
                <div>
                    <style>{summaryRowStyle}</style>
                     <Row style={{ marginBottom: '15px' }}>
                         <Col span={12}>
                             <Button 
                                 type="primary" 
                                 onClick={() => handleExport('excel')}
                                 style={{ marginRight: '10px' }}
                             >
                                 Export to Excel
                             </Button>
                         </Col>
                         <Col span={12}>
                             <Button 
                                 type="primary" 
                                 onClick={() => handleExport('pdf')}
                                 style={{ backgroundColor: '#ff4d4f', borderColor: '#ff4d4f' }}
                             >
                                 Export to PDF
                             </Button>
                         </Col>
                     </Row>
                    <div>
                        <Row>
                            <Col>
                            Trip Name: {modalData.tripDetails.description}
                            </Col>
                        </Row>
                        <Row>
                            <Col span={12}>
                            From: {modalData.tripDetails.origin}
                            </Col>
                            <Col span={12}>
                            To: {modalData.tripDetails.destination}
                            </Col>
                        </Row>
                        <Row>
                            <Col span={12}>
                            Travel Date: {getUIDateFormat(modalData.tripDetails.start_date)}
                            </Col>
                            <Col span={12}>
                            Dep Time: {modalData.tripDetails.start_time}
                            </Col>
                        </Row>
                        <Row>
                            <Col span={12}>
                            Vehicle No: {modalData.vechicleInfo.vechicle_no}
                            </Col>
                        </Row>
                    </div>
                    <div style={{ marginTop: '20px' }}>
                        {Object.keys(populateTableData(modalFilteredData)).map((branch, index) => {
                            const branchData = populateTableData(modalFilteredData)[branch];
                            const summary = calculateBranchSummary(branchData);
                            
                            // Create data source with summary row
                            const dataSourceWithSummary = [...branchData, {
                                key: `${branch}-summary`,
                                agent: 'TOTAL',
                                pnr: '',
                                origin: '',
                                dropCity: '',
                                passengerName: '',
                                phoneNumber: '',
                                seatNumbers: '',
                                totalSeats: summary.totalSeats,
                                totalFare: summary.totalFare,
                                totalGST: summary.totalGST,
                                totalCommission: summary.totalCommission,
                                totalAmountPaid: summary.totalAmountPaid,
                                isSummaryRow: true
                            }];
                            
                            return (
                                <div key={index} style={{ marginBottom: '30px' }}>
                                    <h3 style={{ 
                                        marginBottom: '15px', 
                                        padding: '10px',
                                        backgroundColor: '#f0f0f0',
                                        borderRadius: '4px',
                                        borderLeft: '4px solid #1890ff'
                                    }}>
                                        {branch}
                                    </h3>
                                    <Table 
                                        dataSource={dataSourceWithSummary} 
                                        columns={columns}
                                        pagination={{ pageSize: 10 }}
                                        scroll={{ x: 1200 }}
                                        size="small"
                                        bordered
                                        rowClassName={(record) => record.isSummaryRow ? 'summary-row' : ''}
                                    />
                                    </div>
                            );
                        })}
                    </div>
                </div>
            );
        }
        return null;
    }
    
    if(checkActionAllowed(ACTION_VIEW_GROUP_BY_BRANCH_REPORT_ALLOWED)){
        return (
            <div>
                <Button
                    className="bordered-btn send-driver-details-btn"
                    onClick={showModal}
                >
                    Group By Branch
                </Button>
                <Modal
                    title="Group By Branch"
                    visible={isModalVisible}
                    onCancel={handleCancel}
                    footer={null}
                    width="90%"
                    style={{ top: 20 }}
                    bodyStyle={{ maxHeight: '80vh', overflowY: 'auto' }}
                >
                    {renderModalContent()}
                </Modal>
            </div>
        );
    }else{
        return null;
    }
}

